import { SUPPORTED_CURRENCIES, getCurrencyMetadata } from "@cede/types";
import { CURRENCY_CODE_MAP } from "../currencies";

export interface FormatFiatCurrencyOptions {
  /** The currency code (e.g., "usd", "idr") */
  currencyCode: string;
  /** Whether to use compact notation (K, M, B) */
  compact?: boolean;
  /** Whether to show the currency code after the symbol (e.g., "Rp 15,000 IDR") */
  showCode?: boolean;
  /** Locale for number formatting */
  locale?: string;
}

export interface FormatWithUsdtOptions extends FormatFiatCurrencyOptions {
  /** The conversion rate from USDT to the target currency */
  rate: number;
  /** Whether to show USDT value in parentheses */
  showUsdt?: boolean;
}

/**
 * Get currency symbol from code (case-insensitive)
 */
export function getFiatCurrencySymbol(currencyCode: string): string {
  const upperCode = currencyCode.toUpperCase();
  const metadata = getCurrencyMetadata(currencyCode);
  if (metadata) {
    return metadata.symbol;
  }
  return CURRENCY_CODE_MAP.get(upperCode) || upperCode;
}

/**
 * Get number of decimal places for a currency
 */
export function getCurrencyDecimalPlaces(currencyCode: string): number {
  const metadata = getCurrencyMetadata(currencyCode);
  return metadata?.decimalPlaces ?? 2;
}

/**
 * Format a number in compact notation (K, M, B)
 */
export function formatCompact(value: number, decimalPlaces: number = 2): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (absValue >= 1_000_000_000) {
    return `${sign}${(absValue / 1_000_000_000).toFixed(decimalPlaces)}B`;
  }
  if (absValue >= 1_000_000) {
    return `${sign}${(absValue / 1_000_000).toFixed(decimalPlaces)}M`;
  }
  if (absValue >= 1_000) {
    return `${sign}${(absValue / 1_000).toFixed(decimalPlaces)}K`;
  }
  return `${sign}${absValue.toFixed(decimalPlaces)}`;
}

/**
 * Format a fiat currency value with proper symbol and decimal places
 *
 * @example
 * formatFiatCurrency(1000, { currencyCode: "usd" }) // "$1,000.00"
 * formatFiatCurrency(15800000, { currencyCode: "idr" }) // "Rp 15,800,000"
 * formatFiatCurrency(1500000, { currencyCode: "idr", compact: true }) // "Rp 1.50M"
 */
export function formatFiatCurrency(value: number, options: FormatFiatCurrencyOptions): string {
  const { currencyCode, compact = false, showCode = false, locale = "en-US" } = options;

  const symbol = getFiatCurrencySymbol(currencyCode);
  const decimalPlaces = getCurrencyDecimalPlaces(currencyCode);
  const upperCode = currencyCode.toUpperCase();

  let formattedNumber: string;

  if (compact) {
    formattedNumber = formatCompact(value, Math.min(decimalPlaces, 2));
  } else {
    formattedNumber = value.toLocaleString(locale, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    });
  }

  // Handle negative numbers
  const isNegative = value < 0;
  if (isNegative) {
    formattedNumber = formattedNumber.replace("-", "");
  }

  // Build the formatted string
  const separator = symbol.length > 1 ? " " : "";
  let result = `${symbol}${separator}${formattedNumber}`;

  if (showCode) {
    result = `${result} ${upperCode}`;
  }

  if (isNegative) {
    result = `-${result}`;
  }

  return result;
}

/**
 * Format a USDT value with local currency conversion
 *
 * For non-USD currencies, shows local currency with USDT in parentheses.
 * For USD, shows only the USD value.
 *
 * @example
 * formatUsdtWithLocalCurrency(1000, { currencyCode: "idr", rate: 15800, showUsdt: true })
 * // "Rp 15,800,000 (1,000 USDT)"
 *
 * formatUsdtWithLocalCurrency(1000, { currencyCode: "usd", rate: 1, showUsdt: true })
 * // "$1,000.00"
 */
export function formatUsdtWithLocalCurrency(usdtValue: number, options: FormatWithUsdtOptions): string {
  const { currencyCode, rate, showUsdt = true, compact = false, locale = "en-US" } = options;
  const lowerCode = currencyCode.toLowerCase();

  // For USD, just return the formatted USD value
  if (lowerCode === "usd") {
    return formatFiatCurrency(usdtValue, { currencyCode: "usd", compact, locale });
  }

  // Convert USDT to local currency
  const localValue = usdtValue * rate;

  // Format local currency
  const localFormatted = formatFiatCurrency(localValue, {
    currencyCode,
    compact,
    locale,
  });

  // If not showing USDT, return just the local currency
  if (!showUsdt) {
    return localFormatted;
  }

  // Format USDT value
  const usdtFormatted = usdtValue.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${localFormatted} (${usdtFormatted} USDT)`;
}

/**
 * Format a local currency value showing the USDT equivalent
 * This is the inverse of formatUsdtWithLocalCurrency
 *
 * @example
 * formatLocalWithUsdtEquivalent(15800000, { currencyCode: "idr", rate: 15800, showUsdt: true })
 * // "Rp 15,800,000 (~1,000 USDT)"
 */
export function formatLocalWithUsdtEquivalent(localValue: number, options: FormatWithUsdtOptions): string {
  const { currencyCode, rate, showUsdt = true, compact = false, locale = "en-US" } = options;

  // Format local currency
  const localFormatted = formatFiatCurrency(localValue, {
    currencyCode,
    compact,
    locale,
  });

  // If not showing USDT or rate is 0, return just the local currency
  if (!showUsdt || rate === 0) {
    return localFormatted;
  }

  // Convert local to USDT
  const usdtValue = localValue / rate;

  // Format USDT value
  const usdtFormatted = usdtValue.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return `${localFormatted} (~${usdtFormatted} USDT)`;
}

/**
 * Get a list of all supported currency codes
 */
export function getSupportedCurrencyCodes(): string[] {
  return Object.keys(SUPPORTED_CURRENCIES);
}
